import { TEMPLATE_WEBMCP_LOG_PREFIX } from './constants';

/** The JSON download API supplies CORS headers even when the public XML URL does not. */
export function publicStorageDownloadUrl(src: string): string | null {
  try {
    const url = new URL(src);
    if (
      url.origin !== 'https://storage.googleapis.com' ||
      url.username ||
      url.password ||
      url.search ||
      url.hash ||
      url.pathname.startsWith('/storage/')
    ) {
      return null;
    }
    const match = /^\/([^/]+)\/(.+)$/.exec(url.pathname);
    if (!match) {
      return null;
    }
    const bucket = encodeURIComponent(decodeURIComponent(match[1]));
    const object = encodeURIComponent(decodeURIComponent(match[2]));
    return `${url.origin}/storage/v1/b/${bucket}/o/${object}?alt=media`;
  } catch {
    return null;
  }
}

function checkAbort(signal?: AbortSignal): void {
  if (signal?.aborted) {
    throw signal.reason ?? new DOMException('Preview capture was aborted.', 'AbortError');
  }
}

async function fetchDataUrl(url: string, signal?: AbortSignal): Promise<string> {
  checkAbort(signal);
  try {
    const response = await fetch(url, { signal });
    if (!response.ok) {
      throw new PreviewResourceError(`Preview resource returned HTTP ${response.status}`, url);
    }
    const blob = await response.blob();
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });
    checkAbort(signal);
    return dataUrl;
  } catch (error) {
    checkAbort(signal);
    if (error instanceof PreviewResourceError) {
      throw error;
    }
    // Network and decoding errors can contain the original signed URL. Keep only safe context.
    throw new PreviewResourceError('Preview resource could not be loaded', url);
  }
}

class PreviewResourceError extends Error {
  constructor(message: string, resourceUrl: string) {
    const url = new URL(resourceUrl);
    url.search = '';
    url.hash = '';
    url.username = '';
    url.password = '';
    super(`${message}: ${url.href}`);
  }
}

/** Call only on the off-screen capture tree. No failures are cached between captures. */
export async function inlinePublicStorageImages(
  root: ParentNode,
  signal?: AbortSignal,
): Promise<{ restore: () => void; warnings: string[] }> {
  const downloads = new Map<string, Promise<string>>();
  const restores: Array<() => void> = [];
  const warnings: string[] = [];
  const restore = () => restores.forEach((fn) => fn());
  try {
    await Promise.all(
      Array.from(root.querySelectorAll('img, image')).map(async (image) => {
        const src =
          image instanceof HTMLImageElement
            ? image.srcset
              ? image.currentSrc || image.src
              : image.src
            : image.getAttribute('href') || '';
        const url = publicStorageDownloadUrl(src);
        if (!url) {
          return;
        }
        try {
          let download = downloads.get(url);
          if (!download) {
            download = fetchDataUrl(url, signal);
            downloads.set(url, download);
          }
          const dataUrl = await download;
          if (image instanceof HTMLImageElement) {
            const originalSrc = image.getAttribute('src');
            const originalSrcset = image.getAttribute('srcset');
            restores.push(() => {
              if (image.getAttribute('src') !== dataUrl) {
                return;
              }
              if (originalSrc === null) {
                image.removeAttribute('src');
              } else {
                image.setAttribute('src', originalSrc);
              }
              if (originalSrcset !== null) {
                image.setAttribute('srcset', originalSrcset);
              }
            });
            image.removeAttribute('srcset');
            image.src = dataUrl;
          } else {
            const originalHref = image.getAttribute('href')!;
            const originalXlink = image.getAttributeNS('http://www.w3.org/1999/xlink', 'href');
            restores.push(() => {
              if (image.getAttribute('href') !== dataUrl) {
                return;
              }
              image.setAttribute('href', originalHref);
              if (originalXlink !== null) {
                image.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', originalXlink);
              }
            });
            image.setAttribute('href', dataUrl);
            image.removeAttributeNS('http://www.w3.org/1999/xlink', 'href');
          }
        } catch (error) {
          checkAbort(signal);
          const detail =
            error instanceof PreviewResourceError
              ? error.message
              : new PreviewResourceError('Preview resource could not be loaded', url).message;
          const warning = `Preview image could not be embedded. ${detail}. The image may be missing from this capture.`;
          if (!warnings.includes(warning)) {
            warnings.push(warning);
            console.warn(TEMPLATE_WEBMCP_LOG_PREFIX, 'capture_warning', warning);
          }
          // Keep the original URL for the existing missing-image fallback.
        }
      }),
    );
  } catch (error) {
    restore();
    throw error;
  }
  return { restore, warnings };
}

function fontFamilyNames(value: string): string[] {
  return value.split(',').map((name) =>
    name
      .trim()
      .replace(/^['"]|['"]$/g, '')
      .toLowerCase(),
  );
}

/** Embed readable font faces used by the creative without fetching the shell's stylesheets. */
export async function embedPreviewFonts(
  root: HTMLElement,
  signal?: AbortSignal,
): Promise<{ css: string; warnings: string[] }> {
  checkAbort(signal);
  const doc = root.ownerDocument;
  const families = new Set<string>();
  [root, ...Array.from(root.querySelectorAll('*'))].forEach((node) => {
    fontFamilyNames(getComputedStyle(node).fontFamily).forEach((family) => families.add(family));
  });
  const faces: Array<{ css: string; baseUrl: string; family: string }> = [];
  function collect(rules: CSSRuleList, baseUrl: string): void {
    Array.from(rules).forEach((rule) => {
      if (rule.type === CSSRule.FONT_FACE_RULE) {
        const face = rule as CSSFontFaceRule;
        if (
          fontFamilyNames(face.style.getPropertyValue('font-family')).some((f) => families.has(f))
        ) {
          faces.push({
            css: face.cssText,
            baseUrl,
            family: face.style.getPropertyValue('font-family'),
          });
        }
      } else if ('cssRules' in rule) {
        collect((rule as CSSGroupingRule).cssRules, baseUrl);
      }
    });
  }
  Array.from(doc.styleSheets).forEach((sheet) => {
    try {
      collect(sheet.cssRules, sheet.href || doc.baseURI);
    } catch {
      // Cross-origin shell styles are unreadable and unrelated to the published preview fonts.
    }
  });

  const downloads = new Map<string, Promise<string>>();
  const warnings: string[] = [];
  const embedded = await Promise.all(
    faces.map(async ({ css, baseUrl, family }) => {
      try {
        const matches = Array.from(css.matchAll(/url\(\s*(?:"([^"]*)"|'([^']*)'|([^)]*?))\s*\)/g));
        const replacements = await Promise.all(
          matches.map(async (match) => {
            const src = match[1] ?? match[2] ?? match[3];
            if (src.startsWith('data:')) {
              return match[0];
            }
            const url = new URL(src, baseUrl).href;
            let download = downloads.get(url);
            if (!download) {
              download = fetchDataUrl(url, signal);
              downloads.set(url, download);
            }
            return `url("${await download}")`;
          }),
        );
        // Replace by position so identical URLs and CSS metacharacters remain safe.
        let result = css;
        for (let i = matches.length - 1; i >= 0; i--) {
          const start = matches[i].index!;
          result =
            result.slice(0, start) + replacements[i] + result.slice(start + matches[i][0].length);
        }
        return result;
      } catch (error) {
        checkAbort(signal);
        const detail = error instanceof PreviewResourceError ? ` ${error.message}.` : '';
        const warning = `Preview font ${family} could not be embedded.${detail} Browser fallback fonts may be used for this face.`;
        warnings.push(warning);
        console.warn(TEMPLATE_WEBMCP_LOG_PREFIX, 'capture_warning', warning);
        return '';
      }
    }),
  );
  checkAbort(signal);
  return { css: embedded.filter(Boolean).join('\n'), warnings };
}
